import { createContext, lazy, Suspense, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState, useSyncExternalStore, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { markSessionActivity, usePermission, useRealtime } from "@ncp/api-client";
import { useLocale } from "@ncp/i18n";
import type { HostDisplay, PageRoute } from "@ncp/page-sdk/protocol";
import type { Sensitivity } from "@ncp/page-sdk/desk";
import { installPageEmbed, setActivePageLink, type PageLink, type PageSessionInfo } from "@ncp/page-host";
import { SHELL } from "../app/flags";
import { withLocales } from "../app/locales-ready";
import { AppSkeleton } from "../os/AppSkeleton";
import { DeskSkeleton } from "../econic/desks/DeskSkeleton";
import { readContextLine, readDeskHeader } from "../econic/desks/deskState";
import { useDeskIdentity } from "../econic/desks/identity";
import { useDeskWarmup, WARM_MAX } from "../econic/desks/warmup";
import { parseDisplay } from "../econic/prefs/display";
import { usePrefOptional } from "../econic/prefs";
import { usePinnedDesks } from "../econic/workspaces/pinned";
import { useDocumentTheme } from "../econic/theme";
import { useClassification } from "../econic/shell/classification";
import { usePersonalPages } from "./api";
import { readCopilot, type PageAsk } from "./copilotState";

// The frame host, the desk header and error, and the page's copilot load with the first page kept (a desk, a page of a space), never with
// the shell's first paint: the conversation, markdown and the page bridge stay out of the initial bundle.
const loadPageHost = () => import("@ncp/page-host");
const PageHost = lazy(() => loadPageHost().then((m) => ({ default: m.PageHost })));
const DeskHeader = lazy(() => import("../econic/desks/DeskHeader").then((m) => ({ default: m.DeskHeader })));
const DeskError = lazy(() => import("../econic/desks/DeskError").then((m) => ({ default: m.DeskError })));
const PageCopilot = lazy(withLocales(() => import("./PageCopilot").then((m) => ({ default: m.PageCopilot }))));
const ImproveDialog = lazy(withLocales(() => import("./ImproveDialog").then((m) => ({ default: m.ImproveDialog }))));

const ECONIC = SHELL === "econic";

/**
 * Full-page frames that stay alive after the person moves away, so going back to one is instant and finds it as it was. Adaptive
 * (shell-ia §15.3): 8 on a device with ≥ 8 GB, else 4 (≈ 4.5 MB of JS heap per kept desk, D/_measure/desk-memory.json). LRU; pinned
 * desks (the Workspaces pinned row) are evicted last.
 */
function keepLimit(): number {
  const memory = typeof navigator === "undefined" ? undefined : (navigator as Navigator & { deviceMemory?: number }).deviceMemory;
  return typeof memory === "number" && memory >= 8 ? 8 : 4;
}

export interface KeptPageProps {
  pageKey: string;
  space: string | null;
  entry: string | null;
  route: PageRoute;
  onRoute: (route: PageRoute, replace: boolean) => void;
  onEntry: (entry: string | null) => void;
  onTitle: (title: string) => void;
}

interface Kept {
  props: KeptPageProps;
  title: string | null;
  /** Opened out of view ahead of the person (hover intent or the idle warm-up), not yet shown. */
  warm: boolean;
  session: PageSessionInfo | null;
  /** The page's own raise of the classification banner (HostOp `classification`). */
  raise: { level: Sensitivity; source: string } | null;
  /** F1: a newer build is ready (realtime `page.rebuilt`); null when none or after the person chose later. */
  rebuilt: number | null;
  /** Bumped by "Reload" to open the frame again on the new build. */
  reload: number;
}

interface KeptApi {
  /** What the page showed when the person left it. */
  last(id: string): Kept | null;
  show(id: string, props: KeptPageProps): void;
  hide(id: string): void;
  /**
   * Opens a page's frame out of view ahead of the person (a launcher tile they rest on, a desk number in the desk switcher, the idle
   * warm-up), so the click finds it ready. At most two warm frames, the oldest replaced; none on small screens, data saver or low-memory
   * devices. Showing it later keeps the same frame.
   */
  warm(id: string, props: KeptPageProps): void;
}

/** The page in view: its link (session, client tools) and whether the person may suggest a change to it. */
export interface ActivePage {
  link: PageLink | null;
  canSuggest: boolean;
  suggest: () => void;
}

const KeptContext = createContext<KeptApi | null>(null);
const ActivePageContext = createContext<ActivePage>({ link: null, canSuggest: false, suggest: () => undefined });

export const keptId = (pageKey: string, space: string | null) => `${pageKey}:${space ?? ""}`;

export function useKeptPages(): KeptApi {
  const api = useContext(KeptContext);
  if (!api) throw new Error("useKeptPages must be used inside <KeptPages>.");
  return api;
}

export function useActivePage(): ActivePage {
  return useContext(ActivePageContext);
}

/** Warming costs a session per frame: only where the device can spare it. */
function canWarm(): boolean {
  if (typeof window === "undefined" || !window.matchMedia("(min-width: 1024px)").matches) return false;
  const nav = navigator as Navigator & { connection?: { saveData?: boolean }; deviceMemory?: number };
  if (nav.connection?.saveData) return false;
  if (typeof nav.deviceMemory === "number" && nav.deviceMemory < 4) return false;
  return true;
}

const noop = () => undefined;
const BARE_ROUTE: PageRoute = { path: "", params: {} };

/** The props a warm frame opens with until the page's route shows it (CustomPage then hands the real handlers). */
function warmProps(pageKey: string, space: string | null): KeptPageProps {
  return { pageKey, space, entry: null, route: BARE_ROUTE, onRoute: noop, onEntry: noop, onTitle: noop };
}

/** The device's own accessibility settings that frames are told about (APPLE-SPEC §9.2), live: a change re-sends the `display` event. */
const MEDIA = {
  motion: "(prefers-reduced-motion: reduce)",
  transparency: "(prefers-reduced-transparency: reduce)",
  contrast: "(prefers-contrast: more)",
  forced: "(forced-colors: active)",
} as const;
type MediaFlag = keyof typeof MEDIA;

function subscribeMedia(onChange: () => void): () => void {
  if (typeof window === "undefined" || !window.matchMedia) return () => undefined;
  const lists = Object.values(MEDIA).map((q) => window.matchMedia(q));
  for (const l of lists) l.addEventListener("change", onChange);
  return () => { for (const l of lists) l.removeEventListener("change", onChange); };
}
/** A stable string of the flags that are on ("motion forced"), so the snapshot compares by value. */
function mediaSnapshot(): string {
  if (typeof window === "undefined" || !window.matchMedia) return "";
  return (Object.keys(MEDIA) as MediaFlag[]).filter((k) => window.matchMedia(MEDIA[k]).matches).join(" ");
}

/**
 * The person's display settings as frames take them (shell-ia §9.1, C36; APPLE-SPEC §9.2): "system" choices are resolved against the
 * device here, so the page gets a plain reduce/allow; increased contrast is on when the person or the device asks for it; forced colours
 * come from the device. Device changes are followed live. Outside the ECONIC shell the preferences read as their defaults.
 */
function useFrameDisplay(): HostDisplay | undefined {
  const raw = usePrefOptional<unknown>("ui.display", null);
  const persona = usePrefOptional<string | null>("persona", null);
  const scheme = useDocumentTheme();
  const media = useSyncExternalStore(subscribeMedia, mediaSnapshot, () => "");
  const key = JSON.stringify(raw ?? null);
  return useMemo(() => {
    if (!ECONIC) return undefined;
    const on = new Set(media.split(" "));
    const d = parseDisplay(raw, typeof persona === "string" ? persona : null);
    const motion = d.motion === "system" ? (on.has("motion") ? "reduce" : "allow") : d.motion;
    const transparency = d.transparency === "system" ? (on.has("transparency") ? "reduce" : "allow") : d.transparency;
    const contrast = d.contrast === "more" || on.has("contrast") ? "more" : "normal";
    return { contrast, motion, transparency, density: "comfortable", scheme, text: d.text, forcedColors: on.has("forced") };
    // raw is compared by its JSON
  }, [key, persona, scheme, media]);
}

/**
 * Hosts the frames of full pages (/p/:pageKey) next to the routed screen. The route only says which page is in view; its frame lives here,
 * hidden while another screen shows, until it falls out of the most recent few. Frames keep their DOM position: moving an iframe reloads it.
 *
 * Under the ECONIC shell the page in view gets the host-drawn desk header above its frame (SPEC C4; WP-DESK-HOST), the desk skeleton and
 * error card, the person's display settings, the desk's classification floor (and the page's own raises), the host-fed module cache (F2),
 * the idle warm-up (F3) and the "updated version" bar after a rebuild (F1).
 *
 * Each frame's last state is kept; the one in view feeds the copilot (its starters, its context line and a page's ask, acted on once per
 * nonce). Until a page is ready its frame shows the skeleton.
 */
export function KeptPages({ children }: { children: ReactNode }) {
  const kept = useRef(new Map<string, Kept>());
  const recent = useRef<string[]>([]);
  const warmIds = useRef<string[]>([]);
  const [order, setOrder] = useState<string[]>([]);
  const [active, setActive] = useState<string | null>(null);
  const activeRef = useRef<string | null>(null);
  activeRef.current = active;
  const [, rerender] = useReducer((n: number) => n + 1, 0);
  const links = useRef(new Map<string, PageLink>());
  const states = useRef(new Map<string, Record<string, unknown>>());
  const asked = useRef(new Map<string, string>());
  const [improving, setImproving] = useState(false);
  const navigate = useNavigate();
  const { locale } = useLocale();
  const { can } = usePermission();
  const realtime = useRealtime();
  const display = useFrameDisplay();
  // The Workspaces pinned row is a section of the viewer's `workspaces` layout (WP-WORKSPACES, not a preference): pinned desks leave last.
  const { desks: pinned } = usePinnedDesks({ enabled: ECONIC });
  const pinnedKeys = useMemo(() => new Set(pinned.map((p) => p.pageKey)), [pinned]);
  const pinnedRef = useRef(pinnedKeys);
  pinnedRef.current = pinnedKeys;
  useEffect(() => installPageEmbed(locale, navigate), [locale, navigate]);

  const onLink = useCallback((id: string, link: PageLink | null) => {
    if (link) links.current.set(id, link);
    else links.current.delete(id);
    rerender();
  }, []);
  // Every frame's state is kept; only the one in view re-renders the copilot and the desk header.
  const onState = useCallback((id: string, state: Record<string, unknown>) => {
    states.current.set(id, state);
    if (activeRef.current === id) rerender();
  }, []);
  const onSession = useCallback((id: string, session: PageSessionInfo) => {
    const k = kept.current.get(id);
    if (!k) return;
    k.session = session;
    if (activeRef.current === id) rerender();
  }, []);
  const onClassification = useCallback((id: string, raise: Kept["raise"]) => {
    const k = kept.current.get(id);
    if (!k) return;
    k.raise = raise;
    if (activeRef.current === id) rerender();
  }, []);
  const activeLink = active ? links.current.get(active) ?? null : null;
  useEffect(() => {
    setActivePageLink(activeLink);
    return () => setActivePageLink(null);
  }, [activeLink]);

  // F1: a page rebuilt in the background (`platform.event {type: "page.rebuilt", keys: {page, version}}`) offers its new version to every
  // frame of it that runs an older one; the frame never reloads on its own while the person works.
  useEffect(() => realtime.on<{ type?: string; keys?: Record<string, unknown> }>("platform.event", (e) => {
    if (e?.type !== "page.rebuilt") return;
    const page = typeof e.keys?.page === "string" ? e.keys.page : null;
    const version = typeof e.keys?.version === "number" ? e.keys.version : null;
    if (!page) return;
    let changed = false;
    for (const k of kept.current.values()) {
      if (k.props.pageKey !== page || !k.session) continue;
      if (version !== null && k.session.page.version >= version && !k.session.stale) continue;
      k.rebuilt = version ?? k.session.page.version + 1;
      changed = true;
    }
    if (changed) rerender();
  }), [realtime]);

  // Personal pages are changed by their owners, not through suggestions.
  const personal = usePersonalPages(can("pages.use"));
  const canSuggest = !!activeLink?.space && !!personal.data && !personal.data.some((p) => p.key === activeLink.pageKey);
  const suggest = useCallback(() => setImproving(true), []);
  const activePage = useMemo<ActivePage>(() => ({ link: activeLink, canSuggest, suggest }), [activeLink, canSuggest, suggest]);

  const drop = (id: string) => {
    kept.current.delete(id);
    states.current.delete(id);
    links.current.delete(id);
    warmIds.current = warmIds.current.filter((x) => x !== id);
  };

  const api = useMemo<KeptApi>(() => ({
    last: (id) => kept.current.get(id) ?? null,
    show: (id, props) => {
      const before = kept.current.get(id);
      kept.current.set(id, before ? { ...before, props, warm: false } : { props, title: null, warm: false, session: null, raise: null, rebuilt: null, reload: 0 });
      // A warm frame becomes a kept one where it already is in the DOM, so it does not reload.
      warmIds.current = warmIds.current.filter((x) => x !== id);
      recent.current = [id, ...recent.current.filter((x) => x !== id)];
      const limit = keepLimit();
      if (recent.current.length > limit) {
        // Least recently used first out, pinned desks last; the page being shown always stays.
        const candidates = recent.current.slice(1).reverse();
        const ordered = [...candidates.filter((x) => !pinnedRef.current.has(x.split(":")[0]!)), ...candidates.filter((x) => pinnedRef.current.has(x.split(":")[0]!))];
        for (const gone of ordered.slice(0, recent.current.length - limit)) {
          drop(gone);
          recent.current = recent.current.filter((x) => x !== gone);
        }
      }
      setOrder((o) => {
        const live = o.filter((x) => kept.current.has(x));
        return live.includes(id) ? (live.length === o.length ? o : live) : [...live, id];
      });
      setActive(id);
      rerender();
    },
    hide: (id) => setActive((a) => (a === id ? null : a)),
    warm: (id, props) => {
      if (kept.current.has(id) || !canWarm()) return;
      while (warmIds.current.length >= WARM_MAX) drop(warmIds.current[0]!);
      warmIds.current = [...warmIds.current, id];
      kept.current.set(id, { props, title: null, warm: true, session: null, raise: null, rebuilt: null, reload: 0 });
      setOrder((o) => [...o.filter((x) => kept.current.has(x)), id]);
    },
  }), []);

  // F3: the idle warm-up of the desks this person uses (ECONIC only).
  const warmDesk = useCallback((pageKey: string, space: string | null) => api.warm(keptId(pageKey, space), warmProps(pageKey, space)), [api]);
  useDeskWarmup(warmDesk, ECONIC);

  const activeState = active ? states.current.get(active) ?? null : null;
  const copilot = useMemo(() => readCopilot(activeState), [activeState]);
  const pendingAsk: PageAsk | null = active && copilot.ask && asked.current.get(active) !== copilot.ask.nonce ? copilot.ask : null;
  const onAsked = useCallback((nonce: string) => {
    if (activeRef.current) asked.current.set(activeRef.current, nonce);
  }, []);
  const activeKept = active ? kept.current.get(active) ?? null : null;

  return (
    <KeptContext.Provider value={api}>
      <ActivePageContext.Provider value={activePage}>
        {children}
        {ECONIC && activeKept ? (
          <ActiveDeskHeader key={active!} kept={activeKept} state={activeState} link={activeLink}
            onReload={() => {
              activeKept.rebuilt = null;
              activeKept.reload += 1;
              rerender();
            }}
            onDismiss={() => {
              activeKept.rebuilt = null;
              rerender();
            }}
            onWarm={(pageKey) => warmDesk(pageKey, activeKept.props.space)} />
        ) : null}
        {order.map((id) => {
          const k = kept.current.get(id);
          if (!k) return null;
          const on = id === active;
          const { pageKey, space, entry, route, onRoute, onEntry } = k.props;
          const host = (
            <PageHost pageKey={pageKey} slot="nav" space={space} entry={entry} route={route} onRoute={onRoute} onEntry={onEntry} active={on}
              reloadKey={k.reload}
              placeholder={ECONIC ? <DeskSkeleton /> : <AppSkeleton />}
              renderError={ECONIC ? (error, retry) => <Suspense fallback={null}><DeskError name={k.session?.page.name ?? pageKey} error={error} onRetry={retry} desk={isDesk(pageKey)} /></Suspense> : undefined}
              display={displayFor(display, k.session)}
              classification={ECONIC ? floorOf(pageKey, k.session) : null}
              chromeHeader={ECONIC && isDesk(pageKey) ? "host" : null}
              hostModules={ECONIC}
              learnPrefetch={ECONIC}
              warm={k.warm}
              immersiveTop={ECONIC ? "var(--econic-banner-h, 0px)" : undefined}
              onActivity={markSessionActivity}
              onClassification={(raise) => onClassification(id, raise)}
              onSession={(session) => onSession(id, session)}
              onLink={(link) => onLink(id, link)}
              onState={(state) => onState(id, state)}
              onTitle={(title) => {
                k.title = title;
                k.props.onTitle(title);
              }} />
          );
          const placeholder = ECONIC ? <DeskSkeleton /> : <AppSkeleton />;
          return ECONIC ? (
            <div key={id} hidden={!on} className="es-desk-frame" data-page={pageKey}><Suspense fallback={on ? placeholder : null}>{host}</Suspense></div>
          ) : (
            <div key={id} hidden={!on} className="h-[calc(100dvh-var(--shell-top))] min-h-0 w-full"><Suspense fallback={on ? placeholder : null}>{host}</Suspense></div>
          );
        })}
        {activeLink ? <Suspense fallback={null}><PageCopilot link={activeLink} canSuggest={canSuggest} onSuggest={suggest} copilot={copilot} pendingAsk={pendingAsk} onAsked={onAsked} /></Suspense> : null}
        {activeLink && canSuggest && improving ? <Suspense fallback={null}><ImproveDialog link={activeLink} open={improving} onOpenChange={setImproving} /></Suspense> : null}
      </ActivePageContext.Provider>
    </KeptContext.Provider>
  );
}

/**
 * The display a frame gets: ECONIC Night only for a page that declares it draws dark (page.json `schemes`); every other page stays light in
 * a dark workspace rather than drawing light-only components on dark tokens. The same object is returned for an unchanged result.
 */
const lightCache = new WeakMap<HostDisplay, HostDisplay>();
function displayFor(display: HostDisplay | undefined, session: PageSessionInfo | null): HostDisplay | undefined {
  if (!display || display.scheme !== "dark" || session?.schemes?.includes("dark")) return display;
  let light = lightCache.get(display);
  if (!light) lightCache.set(display, (light = { ...display, scheme: "light" }));
  return light;
}

const isDesk = (pageKey: string) => /^es-/.test(pageKey) && DESK_KEYS.has(pageKey);
const DESK_KEYS = new Set(["es-studies", "es-international-reports", "es-statistical-report", "es-quarterly-report", "es-publications", "es-open-data", "es-scientific-activities", "es-early-warning", "es-recovery-exercises"]);
const SENS = new Set(["public", "internal", "restricted", "confidential"]);

/** The desk's floor: what its space manifest says (through the session), else Internal for every signed-in page (SPEC §3.1). */
function floorOf(_pageKey: string, session: PageSessionInfo | null): Sensitivity {
  const level = session?.classification?.toLowerCase();
  return level && SENS.has(level) ? (level as Sensitivity) : "internal";
}

/** The header of the page in view, with the page's own classification raise (only the page in view raises the banner). */
function ActiveDeskHeader({ kept, state, link, onReload, onDismiss, onWarm }: {
  kept: Kept;
  state: Record<string, unknown> | null;
  link: PageLink | null;
  onReload: () => void;
  onDismiss: () => void;
  onWarm: (pageKey: string) => void;
}) {
  const identity = useDeskIdentity(kept.props.pageKey, kept.props.space, kept.session);
  useClassification(kept.raise?.level ?? null, kept.raise?.source || undefined);
  const header = identity.desk ? readDeskHeader(state, identity.desk, link?.clientTools ?? []) : null;
  return (
    <Suspense fallback={<div className="es-dhead" aria-hidden data-desk-header-pending="" />}>
      <DeskHeader identity={identity} state={header} context={readContextLine(state)} link={link}
        update={kept.rebuilt !== null ? { onReload, onDismiss } : null} onWarm={onWarm} />
    </Suspense>
  );
}
